/**
 * 알림 드롭다운
 *
 * 알림 목록을 표시하고 관리하는 모달 컴포넌트입니다.
 */
import { useRef, useEffect, useCallback } from 'react';
import { useLanguage } from '@/contexts/AppContext';
import { translations, type NotificationTranslations } from './constants';
import { type NotificationDropdownProps } from './types';
import { Header } from './Header';
import { EmptyState } from './EmptyState';
import { NotificationItem } from './NotificationItem';

/**
 * 시간 경과 포맷팅
 */
function formatTimeAgo(timestamp: Date, t: NotificationTranslations): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - timestamp.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return t.justNow;
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes}${t.minutesAgo}`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours}${t.hoursAgo}`;
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days}${t.daysAgo}`;
  }
}

export function NotificationDropdown({
  notifications,
  isOpen,
  onClose,
  onMarkAsRead,
  onMarkAllAsRead,
  onDeleteNotification,
  onDeleteAll,
}: NotificationDropdownProps) {
  const { locale } = useLanguage();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const t = translations[locale];

  /**
   * 외부 클릭 시 닫기
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  /**
   * 시간 포맷팅 콜백
   */
  const handleFormatTimeAgo = useCallback((timestamp: Date) => {
    return formatTimeAgo(timestamp, t);
  }, [t]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/20" onClick={onClose}>
      <div
        ref={dropdownRef}
        className="absolute top-[70px] left-4 right-4 max-w-md mx-auto bg-background rounded-2xl shadow-xl border border-border max-h-[70vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <Header
          t={t}
          notifications={notifications}
          onClose={onClose}
          onMarkAllAsRead={onMarkAllAsRead}
          onDeleteAll={onDeleteAll}
        />

        {/* 알림 목록 */}
        <div className="max-h-[50vh] overflow-y-auto scrollbar-hide">
          {notifications.length === 0 ? (
            <EmptyState t={t} />
          ) : (
            <div className="p-2">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  t={t}
                  formatTimeAgo={handleFormatTimeAgo}
                  onMarkAsRead={onMarkAsRead}
                  onDeleteNotification={onDeleteNotification}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export type { NotificationDropdownProps } from './types';
export type { Notification } from './types';
